import { useEffect } from "react";
import { toast } from "sonner";
import { consumePendingSharedKit, consumePendingSharedBass, consumePendingSharedFx } from "@/lib/shareKit";
import { addCustomKit } from "@/lib/customDrumKits";
import { addCustomBass } from "@/lib/customBassVoices";
import { addCustomFx } from "@/lib/customJsfxPresets";

/**
 * On every app boot, check the URL for `?kit=…` (drum kit) or `?bass=…`
 * (bass voice) payloads. If present, decode + import + surface a Sonner
 * toast with an "Open in <module>" action that dispatches the right event.
 *
 * URL params are consumed (cleared) regardless of import success so a
 * refresh doesn't re-prompt.
 *
 * Renders nothing — purely a side-effect component.
 */
export default function SharedKitImporter() {
  useEffect(() => {
    // 1. Drum kit
    const drumPayload = consumePendingSharedKit();
    if (drumPayload) {
      try {
        const entry = addCustomKit(drumPayload);
        toast.success(`Kit imported: "${entry.label}"`, {
          description: `Based on ${drumPayload.basedOn} · open the Drum Machine to use it`,
          duration: 8000,
          action: {
            label: "Open Drum Machine",
            onClick: () => {
              window.dispatchEvent(new CustomEvent("elpm:open-drum-machine", {
                detail: { kitId: entry.id },
              }));
            },
          },
        });
      } catch (e) {
        toast.error("Couldn't import shared kit", { description: e.message });
      }
    }

    // 2. Bass voice
    const bassPayload = consumePendingSharedBass();
    if (bassPayload) {
      try {
        const entry = addCustomBass(bassPayload);
        toast.success(`Bass imported: "${entry.label}"`, {
          description: `Based on ${bassPayload.basedOn} · open the Bass Player to use it`,
          duration: 8000,
          action: {
            label: "Open Bass Player",
            onClick: () => {
              window.dispatchEvent(new CustomEvent("elpm:open-bass-player", {
                detail: { voiceId: entry.id },
              }));
            },
          },
        });
      } catch (e) {
        toast.error("Couldn't import shared bass", { description: e.message });
      }
    }

    // 3. JSFX preset
    const fxPayload = consumePendingSharedFx();
    if (fxPayload) {
      try {
        const entry = addCustomFx(fxPayload);
        toast.success(`FX imported: "${entry.label}"`, {
          description: `Open the JSFX editor to apply it to any channel`,
          duration: 8000,
          action: {
            label: "Open JSFX Editor",
            onClick: () => {
              window.dispatchEvent(new CustomEvent("elpm:open-jsfx", {
                detail: { fxId: entry.id },
              }));
            },
          },
        });
      } catch (e) {
        toast.error("Couldn't import shared FX", { description: e.message });
      }
    }
  }, []);

  return null;
}
